/**
 * PDP nova (sections/e-pdp-*.liquid).
 *
 * - Galeria: a miniatura leva ao slide e o slide visível marca a miniatura
 *   e o contador "1 / N". A fileira de miniaturas (celular e tablet) rola
 *   por dentro para a atual ficar à vista; no desktop as fotos ficam
 *   empilhadas, sem miniaturas.
 * - Imagem ampliada em <dialog>; sem JS, o link abre o arquivo.
 * - A quantidade da barra fixa acompanha o seletor principal.
 * - Retorno na página depois da compra. A escrita no carrinho e a gaveta
 *   são do e-cart.js: aqui só se lê o fim do envio (aria-busy volta a false
 *   sem erro visível). Mudança no carrinho feita fora da seção (gaveta,
 *   outro botão) tira o aviso, que ficaria velho.
 * - Barra de compra fixa sempre que o botão principal está fora da tela,
 *   acima ou abaixo (Baymard, 2015: resumo e botão fixos em páginas longas),
 *   e fora do rodapé. No celular a foto abre a página e o botão fica abaixo
 *   dela: a barra aparece já no carregamento, sem animação de entrada.
 * - "Como você quer levar?": o botão e a barra fixa seguem a opção marcada
 *   (avulso ou combo). O preço grande continua o do produto do H1.
 * - Resumo de compra fixo ao lado das fotos (desktop): preso abaixo do
 *   cabeçalho quando cabe na tela, pela base quando é mais alto que ela.
 * - Tabela nutricional longa: no celular, 6 linhas e "Ver tabela completa".
 * - Tabela que não cabe no painel: rola de lado e vira região no Tab.
 * - "Você também pode gostar": recomendações do Shopify mais o complemento
 *   dos mais vendidos das outras linhas, buscados quando a seção se
 *   aproxima, sem repetir os produtos que a seção da linha já mostra.
 * - "Vistos recentemente", só com a permissão da Customer Privacy API.
 */

const smoothOrAuto = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

/* ---------- Galeria ---------- */
/* Rola só a fileira de miniaturas (scrollBy nela), nunca a página. */
function revealThumb(list, thumb) {
  if (!list || !thumb) return;
  const listBox = list.getBoundingClientRect();
  const thumbBox = thumb.getBoundingClientRect();
  if (thumbBox.left >= listBox.left && thumbBox.right <= listBox.right) return;
  const offset = thumbBox.left - listBox.left - (listBox.width - thumbBox.width) / 2;
  list.scrollBy({ left: offset, behavior: smoothOrAuto() });
}

/* 0,75: no tablet, a foto que espia ao lado mostra cerca de 64% de si e não vira a atual. */
function observeSlides(track, slides, onCurrent) {
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onCurrent(slides.indexOf(entry.target));
      }
    },
    { root: track, threshold: 0.75 },
  );
  slides.forEach((slide) => observer.observe(slide));
}

function initGallery(gallery) {
  const track = gallery.querySelector('[data-e-pdp-track]');
  const list = gallery.querySelector('[data-e-pdp-thumbs]');
  const slides = [...gallery.querySelectorAll('[data-e-pdp-slide]')];
  const thumbs = [...gallery.querySelectorAll('[data-e-pdp-thumb]')];
  const count = gallery.querySelector('[data-e-pdp-count]');
  if (!track || slides.length < 2) return;
  let current = 0;
  /* No desktop as fotos ficam empilhadas (e-pdp.css): sem rolagem, todas à vista e no Tab. */
  const isCarousel = () => track.scrollWidth > track.clientWidth + 1;

  const setCurrent = (index) => {
    current = index;
    const carousel = isCarousel();
    thumbs.forEach((thumb, thumbIndex) => {
      if (thumbIndex === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    /* No carrossel, só o slide visível entra na ordem de tabulação: as miniaturas navegam. */
    slides.forEach((slide, slideIndex) => {
      slide.querySelector('[data-e-pdp-zoom-open]')?.setAttribute('tabindex', !carousel || slideIndex === index ? '0' : '-1');
    });
    if (count) count.textContent = String(index + 1);
    revealThumb(list, thumbs[index]);
  };

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', (event) => {
      event.preventDefault();
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smoothOrAuto() });
      setCurrent(index);
    });
  });

  observeSlides(track, slides, (index) => {
    if (isCarousel()) setCurrent(index);
  });
  /* Mudou de carrossel para fotos empilhadas (ou o contrário): refaz o Tab. */
  if ('ResizeObserver' in window) new ResizeObserver(() => setCurrent(current)).observe(track);
  setCurrent(0);
}

/* ---------- Imagem ampliada ---------- */
function initZoom(section) {
  const dialog = section.querySelector('[data-e-pdp-zoom]');
  const image = dialog?.querySelector('[data-e-pdp-zoom-image]');
  const note = dialog?.querySelector('[data-e-pdp-zoom-note]');
  if (!dialog || !image || typeof dialog.showModal !== 'function') return;
  let pending = null;

  section.addEventListener('click', (event) => {
    const link = event.target.closest('[data-e-pdp-zoom-open]');
    if (!link) return;
    event.preventDefault();
    /* A imagem do slide, já carregada, aparece na hora; a de 2048 px entra quando chegar. */
    const preview = link.querySelector('img');
    const large = new Image();
    pending = link.href;
    image.src = preview?.currentSrc || link.href;
    image.alt = preview?.alt ?? '';
    /* Cena feita com IA: a nota do slide acompanha a imagem ampliada. */
    if (note) {
      note.textContent = link.dataset.eAiNote ?? '';
      note.hidden = !link.dataset.eAiNote;
    }
    large.addEventListener('load', () => {
      if (dialog.open && pending === link.href) image.src = link.href;
    });
    large.src = link.href;
    dialog.showModal();
  });
  /* Clique fora da imagem ou nela fecha (closedby="any" ainda não existe em todo navegador). */
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target === image) dialog.close();
  });
  dialog.addEventListener('close', () => {
    pending = null;
    image.removeAttribute('src');
  });
}

/* ---------- Quantidade da barra fixa ---------- */
function initQuantity(section) {
  const select = section.querySelector('[data-e-pdp-qty]');
  const mirror = section.querySelector('[data-e-pdp-qty-mirror]');
  if (!select || !mirror) return;
  const sync = () => {
    mirror.value = select.value;
  };
  select.addEventListener('change', sync);
  sync(); /* o navegador pode restaurar o seletor ao voltar pelo histórico */
}

/* ---------- Retorno depois da compra ---------- */
function initAdded(section) {
  const added = section.querySelector('[data-e-pdp-added]');
  const announce = section.querySelector('[data-e-pdp-announce]');
  if (!added) return;
  const message = added.querySelector('p')?.textContent.trim() ?? '';
  const hide = () => {
    added.hidden = true;
    if (announce) announce.textContent = '';
  };

  section.querySelectorAll('form[data-e-product-form]').forEach((form) => {
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-e-form-error]');
    if (!button) return;
    new MutationObserver(() => {
      if (button.getAttribute('aria-busy') === 'true') {
        hide();
        return;
      }
      if (error && !error.hidden) return;
      added.hidden = false;
      if (announce) announce.textContent = message;
    }).observe(button, { attributes: true, attributeFilter: ['aria-busy'] });
  });

  /* Evento padrão das escritas no carrinho (Shopify.actions.updateCart); o
     e-cart.js dispara o da compra no próprio formulário, os outros na gaveta. */
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (section.contains(event.target)) return;
    hide();
  });

  /* Com um combo no carrinho, qualquer linha mexida na gaveta vai pela Ajax
     API, sem esse evento (e-cart.js, changeBundleLine). O contador do cabeçalho
     muda nas duas rotas. A compra daqui também o muda, com o botão ocupado: aí
     vale o observador do botão. */
  const count = document.querySelector('[data-e-cart-count]');
  if (!count) return;
  let lastCount = count.textContent;
  new MutationObserver(() => {
    if (count.textContent === lastCount) return;
    lastCount = count.textContent;
    if (section.querySelector('form[data-e-product-form] [aria-busy="true"]')) return;
    hide();
  }).observe(count, { childList: true, characterData: true, subtree: true });
}

/* ---------- Barra de compra fixa ---------- */
function initSticky(section) {
  const bar = section.querySelector('[data-e-pdp-sticky]');
  const cta = section.querySelector('[data-e-pdp-form] [type="submit"]');
  if (!bar || !cta || !('IntersectionObserver' in window)) return;

  const footer = document.querySelector('footer');
  const header = document.querySelector('[data-e-header]');
  const headerHeight = Math.round(header?.getBoundingClientRect().height ?? 0);
  let ctaOut = false;
  let footerInView = false;
  let first = true;

  const update = () => {
    const show = ctaOut && !footerInView;
    /* Primeira leitura: a barra já está no lugar, sem subir da borda. */
    if (first) bar.style.transition = 'none';
    bar.classList.toggle('is-visible', show);
    bar.inert = !show;
    if (first) {
      bar.getBoundingClientRect();
      bar.style.removeProperty('transition');
      first = false;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.target === cta) ctaOut = !entry.isIntersecting;
        else footerInView = entry.isIntersecting;
      }
      update();
    },
    { rootMargin: `-${headerHeight}px 0px 0px 0px` },
  );
  observer.observe(cta);
  if (footer) observer.observe(footer);
}

/* ---------- Como você quer levar? ----------
   O rádio marcado vai no envio do formulário principal (FormData). O botão
   troca o texto; a barra fixa troca nome, preço, miniatura, id e rótulo
   acessível pelos do combo marcado (data-e-pdp-take-*, e-pdp-take.liquid) e
   volta aos do produto com o avulso. */
/* Partes da barra que a opção troca, com o que elas mostram no avulso. */
function readBar(bar) {
  if (!bar) return null;
  const parts = {
    name: bar.querySelector('.e-pdp-sticky__name'),
    price: bar.querySelector('[data-e-pdp-sticky-price]'),
    thumb: bar.querySelector('.e-pdp-sticky__thumb'),
    id: bar.querySelector('[data-e-pdp-sticky-id]'),
    button: bar.querySelector('[type="submit"]'),
  };
  const single = {
    name: [...(parts.name?.childNodes ?? [])].map((node) => node.cloneNode(true)),
    price: parts.price?.textContent ?? '',
    srcset: parts.thumb?.getAttribute('srcset'),
    aria: parts.button?.getAttribute('aria-label'),
  };
  return { parts, single };
}

/* Nome do combo com a descrição em itálico, como no título do produto. */
function comboName({ ePdpTakeName: name, ePdpTakeDescriptor: descriptor }) {
  if (!descriptor) return [name];
  const emphasis = document.createElement('em');
  emphasis.textContent = descriptor;
  return [name, ' ', emphasis];
}

function paintBar({ parts, single }, radio) {
  const data = radio.dataset;
  const combo = Boolean(data.ePdpTakeName);
  if (parts.id) parts.id.value = radio.value;
  parts.name?.replaceChildren(...(combo ? comboName(data) : single.name.map((node) => node.cloneNode(true))));
  if (parts.price) parts.price.textContent = combo ? data.ePdpTakePrice : single.price;
  const srcset = combo ? data.ePdpTakeThumb : single.srcset;
  if (parts.thumb && srcset) parts.thumb.setAttribute('srcset', srcset);
  const aria = combo ? `${data.ePdpTakeCta}: ${data.ePdpTakeTitle}` : single.aria;
  if (aria) parts.button?.setAttribute('aria-label', aria);
}

/* Botão ocupado ou em "No carrinho": o e-cart.js devolve depois o rótulo
   guardado em data-e-idle-label, então a troca vai para ele. */
function setIdleLabel(button, text) {
  const label = button?.querySelector('[data-e-label]');
  if (!label || !text) return;
  if (button.dataset.eIdleLabel !== undefined) button.dataset.eIdleLabel = text;
  else label.textContent = text;
}

/* Teto da opção marcada (data-e-pdp-take-max, estoque com venda travada no
   zero): as quantidades acima dele ficam desabilitadas e, se a escolhida
   passou, ela desce para a maior permitida. O "change" leva à barra fixa. */
function limitQuantity(select, max) {
  if (!select) return;
  const limit = Number(max) || Infinity;
  for (const option of select.options) option.disabled = Number(option.value) > limit;
  if (!select.selectedOptions[0]?.disabled) return;
  const allowed = [...select.options].filter((option) => !option.disabled);
  select.value = allowed.at(-1)?.value ?? select.value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function initTake(section) {
  const take = section.querySelector('[data-e-pdp-take]');
  if (!take) return;
  const cta = section.querySelector('[data-e-pdp-form] [type="submit"]');
  const quantity = section.querySelector('[data-e-pdp-qty]');
  const bar = readBar(section.querySelector('[data-e-pdp-sticky]'));
  const apply = (radio) => {
    setIdleLabel(cta, radio.dataset.ePdpTakeCta);
    limitQuantity(quantity, radio.dataset.ePdpTakeMax);
    if (bar) paintBar(bar, radio);
  };

  take.addEventListener('change', (event) => {
    if (event.target.matches('input[type="radio"]')) apply(event.target);
  });
  /* O navegador pode restaurar o rádio marcado ao voltar pelo histórico. */
  const checked = take.querySelector('input[type="radio"]:checked');
  if (checked) apply(checked);
}

/* ---------- Resumo fixo ao lado das fotos (desktop) ----------
   position: sticky no e-pdp.css; aqui só a distância do topo. Cabe na tela:
   24 px abaixo do cabeçalho. Mais alto que ela: top negativo, e o resumo
   rola com a página até a base aparecer (24 px acima do fim da tela) e fica
   preso por ela; botão e condições nunca ficam escondidos. */
const SUMMARY_GAP = 24;
/* Um só ouvinte de resize: o editor de temas recria a seção, e a cópia
   antiga sai do conjunto na primeira medida depois de sair da página. */
const summaryPlacers = new Set();
window.addEventListener('resize', () => summaryPlacers.forEach((place) => place()), { passive: true });

function initSummarySticky(section) {
  const summary = section.querySelector('[data-e-pdp-summary]');
  if (!summary || !('ResizeObserver' in window)) return;
  const header = document.querySelector('[data-e-header]');
  const place = () => {
    if (!summary.isConnected) {
      summaryPlacers.delete(place);
      return;
    }
    const below = (header?.offsetHeight ?? 0) + SUMMARY_GAP;
    const top = Math.min(below, window.innerHeight - summary.offsetHeight - SUMMARY_GAP);
    summary.style.setProperty('--e-pdp-summary-top', `${Math.round(top)}px`);
  };
  new ResizeObserver(place).observe(summary);
  summaryPlacers.add(place);
}

/* ---------- Tabela nutricional longa (celular) ----------
   e-pdp-nutrition-table.liquid marca a tabela com mais de 7 linhas; aqui ela
   fecha em 6 (o CSS só esconde abaixo de 48em) e o botão abre e fecha. */
function initPanelFold(panel) {
  const button = panel.querySelector('[data-e-pdp-panel-more]');
  if (!button) return;
  const openLabel = button.textContent;
  const lessLabel = button.dataset.eLessLabel ?? openLabel;
  panel.setAttribute('data-e-pdp-panel-folded', '');
  button.hidden = false;
  button.addEventListener('click', () => {
    const folded = panel.toggleAttribute('data-e-pdp-panel-folded');
    button.setAttribute('aria-expanded', String(!folded));
    button.textContent = folded ? openLabel : lessLabel;
  });
}

/* ---------- Tabela que rola de lado ----------
   Só a tabela que não cabe no painel vira parada do Tab (região rotulada pelo
   título e pelo produto, que no combo distingue as duas tabelas; as setas
   rolam) e ganha a sombra da borda (CSS em [tabindex]). Se a região focada
   deixa de rolar, fica com tabindex -1 até perder o foco: o foco não cai no
   body. Observa a região (largura da janela, dobra da tabela) e a tabela
   (troca de fonte). */
function initPanelScroll(region) {
  const text = (element) => element?.textContent.replace(/\s+/g, ' ').trim() ?? '';
  const title = text(region.querySelector('.e-pdp-panel__title'));
  const item = text(region.closest('.e-pdp-label__item')?.querySelector('h3'));
  const label = item ? `${title}, ${item}` : title;
  const sync = () => {
    if (region.scrollWidth > region.clientWidth) {
      region.setAttribute('tabindex', '0');
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', label);
    } else if (document.activeElement === region) {
      region.setAttribute('tabindex', '-1');
    } else {
      region.removeAttribute('tabindex');
      region.removeAttribute('role');
      region.removeAttribute('aria-label');
    }
  };
  sync();
  region.addEventListener('focusout', sync);
  if (!('ResizeObserver' in window)) return;
  const observer = new ResizeObserver(sync);
  observer.observe(region);
  if (region.firstElementChild) observer.observe(region.firstElementChild);
}

/* ---------- Variantes (fallback: o catálogo atual tem variante única) ---------- */
function initVariant(section) {
  const select = section.querySelector('[data-e-pdp-variant]');
  if (!select) return;
  select.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    window.location.assign(url);
  });
}

/* ---------- Seções carregadas perto da tela ---------- */
/* Chama load uma vez, quando a seção fica a uma tela de distância. A seção
   oculta não tem caixa: observa o invólucro dela, de altura zero. */
function whenNear(section, load) {
  if (!('IntersectionObserver' in window)) {
    load();
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      load();
    },
    { rootMargin: '100% 0px' },
  );
  observer.observe(section.parentElement ?? section);
}

/* Mostra a seção com os cards novos; e-core.js só observa a revelação no carregamento. */
function showCards(section, list, items) {
  list.replaceChildren(...items);
  section.hidden = false;
  requestAnimationFrame(() => list.querySelectorAll('.e-reveal').forEach((card) => card.classList.add('is-in')));
}

/* ---------- Você também pode gostar ----------
   Regra completa no comentário de sections/e-pdp-related.liquid: primeiro as
   recomendações do Shopify, sem o que a linha já mostra e sem os combos que
   a página já oferece ("Como você quer levar?", e-pdp-take, ou "Combine e
   economize", e-pdp-bundle); se faltar card, o
   complemento (mais vendidos das outras linhas) que veio no <template>. No
   máximo data-combo-limit combos, na ordem do Shopify. */
function initRecommendations(section) {
  const holder = section.querySelector('[data-e-pdp-recommendations]');
  const list = holder?.querySelector('[data-e-pdp-list]');
  if (!holder?.dataset.url || !list) return;
  const bundled = [...document.querySelectorAll('[data-e-pdp-bundle-combo]')].map((offer) => offer.dataset.ePdpBundleCombo);
  const exclude = new Set([...(holder.dataset.exclude ?? '').split(',').filter(Boolean), ...bundled]);
  const limit = Number(holder.dataset.limit) || 4;
  const comboLimit = Number(holder.dataset.comboLimit ?? 2);
  const fill = section.querySelector('template[data-e-pdp-fill]');

  whenNear(section, async () => {
    let related = [];
    try {
      const response = await fetch(holder.dataset.url);
      if (!response.ok) throw new Error(`Recomendações responderam ${response.status}`);
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html');
      related = [...fresh.querySelectorAll('[data-e-pdp-recommendation]')];
    } catch {
      /* Sem recomendações do Shopify, fica só o complemento. */
    }
    const complement = fill ? [...fill.content.querySelectorAll('[data-e-pdp-recommendation]')] : [];
    const picked = [];
    const used = new Set(exclude);
    let combos = 0;
    for (const item of [...related, ...complement]) {
      if (picked.length >= limit) break;
      if (used.has(item.dataset.handle)) continue;
      const isCombo = item.hasAttribute('data-combo');
      if (isCombo && combos >= comboLimit) continue;
      used.add(item.dataset.handle);
      if (isCombo) combos += 1;
      picked.push(item);
    }
    if (picked.length < 2) return;
    showCards(section, list, picked.map((item) => document.importNode(item, true)));
  });
}

/* ---------- Vistos recentemente ----------
   Os handles ficam só no navegador (localStorage, até 8, do mais recente
   para o mais antigo). Grava e lê só quando a Customer Privacy API da
   Shopify permite o uso de preferências (shopify.dev/docs/api/customer-
   privacy: loadFeatures 'consent-tracking-api' 0.1 e
   preferencesProcessingAllowed). Sem a API ou sem permissão, nada é gravado
   e a seção não aparece; sem permissão, a lista antiga também é apagada.
   Cada card vem pronto e atual pela Section Rendering API
   (sections/e-pdp-card.liquid). */
const RECENT_KEY = 'e-pdp-recent';
const RECENT_MAX = 8;

function readRecent() {
  try {
    const handles = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(handles) ? handles.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(handles) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(handles.slice(0, RECENT_MAX)));
  } catch {
    /* Armazenamento bloqueado: a seção só não terá histórico. */
  }
}

function forgetRecent() {
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    /* Armazenamento bloqueado: não há o que apagar. */
  }
}

/* Chama allowed() quando as preferências podem ser processadas, agora ou
   depois que a pessoa responder ao banner (evento visitorConsentCollected). */
function whenPreferencesAllowed(allowed) {
  if (typeof window.Shopify?.loadFeatures !== 'function') return;
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) return;
    const check = () => {
      if (window.Shopify.customerPrivacy?.preferencesProcessingAllowed()) allowed();
      else forgetRecent();
    };
    document.addEventListener('visitorConsentCollected', check);
    check();
  });
}

async function fetchRecentCard(handle) {
  const root = window.Shopify?.routes?.root ?? '/';
  try {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=e-pdp-card`);
    /* Produto que saiu da loja: 404 no ar; na prévia do theme dev, 200 sem o card. */
    if (response.status === 404) return { handle, gone: true };
    if (!response.ok) return { handle };
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const item = page.querySelector('[data-e-pdp-card]');
    return item ? { handle, item } : { handle, gone: true };
  } catch {
    return { handle };
  }
}

function initRecent(section) {
  const list = section.querySelector('[data-e-pdp-recent-list]');
  const current = section.dataset.handle;
  if (!list || !current) return;
  const limit = Number(section.dataset.limit) || 4;
  let started = false;

  whenPreferencesAllowed(() => {
    if (started) return;
    started = true;
    const earlier = readRecent().filter((handle) => handle !== current);
    writeRecent([current, ...earlier]);
    if (earlier.length === 0) return;

    whenNear(section, async () => {
      const results = await Promise.all(earlier.slice(0, limit).map(fetchRecentCard));
      /* Produto que saiu da loja sai também da lista. */
      const gone = new Set(results.filter((result) => result.gone).map((result) => result.handle));
      if (gone.size > 0) writeRecent(readRecent().filter((handle) => !gone.has(handle)));
      const items = results.map((result) => result.item).filter(Boolean);
      if (items.length > 0) showCards(section, list, items);
    });
  });
}

/* ---------- Início ---------- */
function init(scope) {
  scope.querySelectorAll('[data-e-pdp]').forEach((section) => {
    if (section.dataset.ePdpReady) return;
    section.dataset.ePdpReady = 'true';
    section.querySelectorAll('[data-e-pdp-gallery]').forEach(initGallery);
    initZoom(section);
    initQuantity(section);
    initAdded(section);
    initSticky(section);
    initTake(section);
    initSummarySticky(section);
    initVariant(section);
  });
  scope.querySelectorAll('[data-e-pdp-panel-fold]').forEach((panel) => {
    if (panel.dataset.ePdpReady) return;
    panel.dataset.ePdpReady = 'true';
    initPanelFold(panel);
  });
  scope.querySelectorAll('[data-e-pdp-scroll]').forEach((region) => {
    if (region.dataset.ePdpReady) return;
    region.dataset.ePdpReady = 'true';
    initPanelScroll(region);
  });
  scope.querySelectorAll('[data-e-pdp-related]').forEach((section) => {
    if (section.dataset.ePdpReady) return;
    section.dataset.ePdpReady = 'true';
    initRecommendations(section);
  });
  scope.querySelectorAll('[data-e-pdp-recent]').forEach((section) => {
    if (section.dataset.ePdpReady) return;
    section.dataset.ePdpReady = 'true';
    initRecent(section);
  });
}

init(document);
/* O editor de temas re-renderiza a seção: a nova cópia precisa ser ligada. */
document.addEventListener('shopify:section:load', (event) => init(event.target));
